import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, InputSignal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedTagApiService } from '@shared/api/api-loop/services/shared-tag-api.service';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { ConversationHelperService } from '@shared/modules/conversations/services/conversation-helper-service/conversation-helper.service';
import { SharedTagActionsService } from '@shared/modules/shared-tag/data-access/shared-tag-actions/shared-tag-actions.service';
import { JoinPipeModule } from '@shared/ui/pipes/join/join-pipe.module';
import { LoopTooltipModule } from '@shared/ui/tooltip/tooltip.module';
import { HasReactionsPipe } from '@shared/modules/comments/components/comment-reactions/internal/has-reactions.pipe';
import { GetReactionTagsPipe } from '@shared/modules/comments/components/comment-reactions/internal/get-reaction-tags.pipe';
import { SharedTagReactionModel } from '@dta/shared/models-api-loop/shared-tag/shared-tag.model';
import { CommentModel } from '@dta/shared/models-api-loop/comment/comment.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'loop-comment-reactions',
  templateUrl: './comment-reactions.component.html',
  styleUrls: ['./comment-reactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoopTooltipModule, JoinPipeModule, HasReactionsPipe, GetReactionTagsPipe, AsyncPipe]
})
export class CommentReactionsComponent {
  private readonly conversationHelperService: ConversationHelperService = inject(ConversationHelperService);
  private readonly sharedTagApiService: SharedTagApiService = inject(SharedTagApiService);
  private readonly sharedTagActionsService: SharedTagActionsService = inject(SharedTagActionsService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);

  private readonly userIdSignal: Signal<string | null> = toSignal(
    this.userAuthService.currentSession$.pipe(map(session => session.user.id)),
    { initialValue: null }
  );

  readonly commentSignal: InputSignal<CommentModel> = input.required<CommentModel>();
  readonly conversationIdSignal: InputSignal<string | undefined> = input<string | undefined>(undefined);

  toggleReaction(reaction: string): void {
    this.conversationHelperService.markConversationAsRead({
      conversationId: this.conversationIdSignal(),
      isForce: true
    });

    const userId = this.userIdSignal();
    if (!userId) {
      return;
    }

    const comment = this.commentSignal();
    const reactionTag = SharedTagReactionModel.buildReactionSharedTag(reaction, userId);
    comment.addOrRemoveReactionSharedTag(reactionTag);

    this.sharedTagApiService
      .SharedTag_UpdateTag({ tags: comment.sharedTags })
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => {
        this.sharedTagActionsService.sharedTagsUpdated$.next(comment.sharedTags);
      });
  }
}
